import { Fragment, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Info, Lock, PanelLeftClose, PanelLeftOpen, ScrollText } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useMe, useUpdateUiPrefs } from '@/features/auth/hooks';
import { useOnboardingState } from '@/features/dashboard/hooks';
import { cardGate, navTabLocked, termsGateActive } from '@/features/dashboard/onboarding';
import { cn } from '@/lib/utils';
import { visibleNavGroups } from './nav-items';

/**
 * Sidebar — primary navigation rail (prototype replacing the Subnav tab
 * strip). Spans the full height of the shell row (AppShell pins it; only
 * the content column scrolls), with the link list scrolling independently
 * if it ever outgrows the viewport and a collapse toggle pinned at the
 * bottom.
 *
 * Collapsed mode is an icon-only rail (w-16): group labels become thin
 * separators, links center their icon and keep the label as a title
 * tooltip. Collapse state persists as a SERVER-side user preference
 * (ui_prefs.sidebar_collapsed — browser storage is barred by project
 * rule): the toggle flips local state instantly and fire-and-forgets the
 * PATCH; /me seeds the initial state on the next load.
 *
 * First-run gating is the SAME logic the tab strip used — `navTabLocked`
 * + `cardGate` from features/dashboard/onboarding.ts — so locked entries
 * render as muted, non-navigable labels with the lock + reason tooltip.
 *
 * Desktop-only for the prototype (`hidden lg:flex`); mobile chrome is out
 * of scope for this UI experiment.
 */
const LINK_BASE =
  'flex items-center gap-2.5 rounded-md px-3 py-2 text-[13.5px] font-medium text-silver/85 border-l-2 border-transparent no-underline transition-colors duration-150 hover:text-white hover:bg-white/10';

const LINK_ACTIVE =
  'flex items-center gap-2.5 rounded-md px-3 py-2 text-[13.5px] font-semibold text-white bg-white/10 border-l-2 border-electric no-underline';

const LINK_LOCKED =
  'flex items-center gap-2.5 rounded-md px-3 py-2 text-[13.5px] font-medium text-silver/40 border-l-2 border-transparent cursor-not-allowed select-none';

export function Sidebar() {
  const me = useMe();
  const updatePrefs = useUpdateUiPrefs();
  // Saved preference is the default; an in-session toggle overrides it
  // immediately (no waiting on the PATCH round-trip).
  const [collapsedOverride, setCollapsedOverride] = useState<boolean | null>(null);
  const collapsed =
    collapsedOverride ?? me.data?.ui_prefs.sidebar_collapsed ?? false;
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsedOverride(next);
    updatePrefs.mutate({ sidebar_collapsed: next });
  };

  // Shared first-run gate — same hook as the dashboard + mobile drawer.
  const onboarding = useOnboardingState();

  // The legal-review footer link is for users who've ALREADY accepted (it opens
  // the Terms in read-only review mode). While the acceptance gate is active the
  // accept screen itself is the Terms, so the footer would be redundant — hide
  // it until accepted.
  const showTermsLink = !termsGateActive(onboarding);

  const iconOnly = collapsed ? 'justify-center px-0 gap-0' : '';

  return (
    <aside
      aria-label="Main sections"
      className={cn(
        // Same gradient family as the Header so the rail + navbar read as one
        // continuous chrome frame (vertical run; the header's is 135°).
        'hidden lg:flex h-full shrink-0 flex-col border-r border-black/15 bg-[linear-gradient(180deg,var(--color-navbar-bg-1),var(--color-navbar-bg-2))] transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <div
        className={cn(
          'shrink-0 flex items-center border-b border-white/10 py-1.5',
          collapsed ? 'justify-center px-2' : 'justify-end px-3',
        )}
      >
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="flex items-center justify-center size-8 rounded-md text-silver/85 cursor-pointer bg-transparent transition-colors duration-150 hover:text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-electric focus-visible:outline-offset-2"
        >
          {collapsed ? (
            <PanelLeftOpen className="size-4" aria-hidden="true" />
          ) : (
            <PanelLeftClose className="size-4" aria-hidden="true" />
          )}
        </button>
      </div>

      <div className={cn('flex-1 overflow-y-auto pb-4 flex flex-col gap-1', collapsed ? 'px-2' : 'px-3')}>
        {visibleNavGroups(me.data).map((group, groupIndex) => (
          <nav key={group.label} aria-label={group.label}>
            {collapsed ? (
              groupIndex > 0 && <div className="border-t border-white/10 my-2 mx-1" />
            ) : (
              <div
                data-tour={group.tourKey}
                className="px-3 pt-5 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-silver/60"
              >
                {group.label}
              </div>
            )}
            <div className={cn('flex flex-col gap-0.5', collapsed && groupIndex === 0 && 'pt-3')}>
              {group.items.map((item) => {
                const Icon = item.icon;
                // Status note shown after the label in the tooltip header
                // (Coming soon / the onboarding-lock reason / nothing).
                const locked = !item.soon && navTabLocked(item.to, onboarding);
                // A locked gate WITHOUT a note (the admin Simulators switch)
                // renders quietly: greyed row, no Lock icon, no reason in the
                // tooltip. Onboarding gates always carry a note.
                const note = item.soon
                  ? 'Coming soon'
                  : locked
                    ? (cardGate(item.to, onboarding).note ?? null)
                    : null;

                const tipContent = (
                  <TooltipContent side="right" className="max-w-64">
                    <div className="text-[12px] font-semibold text-navy">
                      {item.label}
                      {note && <span className="font-medium text-muted"> — {note}</span>}
                    </div>
                    <p className="mt-0.5 text-muted">{item.description}</p>
                  </TooltipContent>
                );

                // Expanded rows: a trailing ⓘ is the ONLY tooltip trigger, so
                // the description card doesn't pop on every row hover. A real
                // <button> (not a span) so keyboard users can Tab to it —
                // Radix opens the tooltip on focus. On link rows it OVERLAYS
                // the Link (absolute, sibling) rather than nesting inside it:
                // button-in-anchor is invalid HTML and a click would navigate.
                // HOVER-REVEAL: invisible at rest (experienced users get a
                // clean rail), fades in while its row is hovered — and snaps
                // visible on keyboard focus so Tab users never lose it.
                const infoTrigger = () =>
                  !collapsed && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={`About ${item.label}`}
                          className={cn(
                            'shrink-0 p-0.5 bg-transparent border-0 text-silver/40 cursor-help rounded-sm',
                            'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-[opacity,color] duration-150',
                            'hover:text-white focus-visible:outline-2 focus-visible:outline-electric focus-visible:outline-offset-1',
                            // Every row type places the icon the same way — plain flex
                            // ml-auto, no absolute/transform positioning — so the enabled
                            // rows land exactly where the soon/locked rows do. Locked rows
                            // keep the icon right after the Lock (no auto-margin).
                            (!locked || note == null) && 'ml-auto',
                          )}
                        >
                          <Info className="size-3.5" aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      {tipContent}
                    </Tooltip>
                  );

                let row: ReactNode;
                if (item.soon) {
                  row = (
                    <span id={item.id} data-tour={item.tourKey} className={cn(LINK_LOCKED, iconOnly, 'group')} aria-disabled="true">
                      <Icon className="size-4 shrink-0" aria-hidden="true" />
                      {!collapsed && (
                        <>
                          <span className="truncate">{item.label}</span>
                          {infoTrigger()}
                        </>
                      )}
                    </span>
                  );
                } else if (locked) {
                  row = (
                    <span id={item.id} data-tour={item.tourKey} className={cn(LINK_LOCKED, iconOnly, 'group')} aria-disabled="true">
                      <Icon className="size-4 shrink-0" aria-hidden="true" />
                      {!collapsed && (
                        <>
                          <span className="truncate">{item.label}</span>
                          {note != null && (
                            <Lock className="size-3.5 shrink-0 ml-auto" aria-hidden="true" />
                          )}
                          {infoTrigger()}
                        </>
                      )}
                    </span>
                  );
                } else {
                  // Chrome (hover/active/border) lives on the WRAPPER, not the Link, so
                  // the info <button> can sit as a real flex sibling of the <a> — a
                  // button inside an anchor is invalid HTML — and use the identical
                  // ml-auto placement the soon/locked rows use. Active state is driven
                  // off the Link's data-status via `has-[]` instead of activeProps.
                  row = (
                    <div
                      className={cn(
                        'group flex items-center gap-2.5 rounded-md px-3 py-2 text-[13.5px] font-medium text-silver/85',
                        'border-l-2 border-transparent transition-colors duration-150 hover:text-white hover:bg-white/10',
                        // Active = highlight + bold white text, NO electric accent bar:
                        // the original active style intended a border-electric bar but it
                        // never rendered (base border-transparent won the CSS tie), so the
                        // rows have always shown a plain highlight — match that.
                        'has-[[data-status=active]]:font-semibold has-[[data-status=active]]:text-white',
                        'has-[[data-status=active]]:bg-white/10',
                        iconOnly,
                      )}
                    >
                      <Link
                        id={item.id}
                        data-tour={item.tourKey}
                        to={item.to}
                        className={cn(
                          'flex flex-1 items-center gap-2.5 min-w-0 no-underline text-inherit',
                          collapsed && 'justify-center gap-0',
                        )}
                      >
                        <Icon className="size-4 shrink-0" aria-hidden="true" />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </Link>
                      {infoTrigger()}
                    </div>
                  );
                }

                // Collapsed: the row IS the icon, so the tooltip lives on it.
                if (collapsed) {
                  return (
                    <Tooltip key={item.id}>
                      <TooltipTrigger asChild>{row}</TooltipTrigger>
                      {tipContent}
                    </Tooltip>
                  );
                }
                return <Fragment key={item.id}>{row}</Fragment>;
              })}
            </div>
          </nav>
        ))}
      </div>

      {/* Footer: always-available link to review the accepted Terms (opens the
          terms screen in read-only review mode). Pinned below the nav list. */}
      {showTermsLink &&
        (collapsed ? (
          <div className="shrink-0 border-t border-white/10 px-2 py-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/onboarding/terms"
                  aria-label="Terms & Conditions"
                  data-tour="nav-terms"
                  className={cn(LINK_BASE, 'justify-center px-0 gap-0')}
                  activeProps={{ className: cn(LINK_ACTIVE, 'justify-center px-0 gap-0') }}
                >
                  <ScrollText className="size-4 shrink-0" aria-hidden="true" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-64">
                <div className="text-[12px] font-semibold text-navy">Terms &amp; Conditions</div>
                <p className="mt-0.5 text-muted">
                  Review the legal documents currently in effect for your account.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <div className="shrink-0 border-t border-white/10 px-3 py-2">
            <Link
              to="/onboarding/terms"
              data-tour="nav-terms"
              className={cn(LINK_BASE, 'text-[12.5px] text-silver/70')}
              activeProps={{ className: LINK_ACTIVE }}
            >
              <ScrollText className="size-4 shrink-0" aria-hidden="true" />
              <span className="truncate">Terms &amp; Conditions</span>
            </Link>
          </div>
        ))}
    </aside>
  );
}
